import React, {
  useState,
  useEffect
} from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link as RouterLink,
} from "react-router-dom";
import moment from 'moment';

import { makeStyles } from '@material-ui/core/styles';
import {
  Chip,
  Avatar,
  Paper,
  Breadcrumbs,
  Link,
  Grid,
  Typography,
  Button,
  IconButton,
  Divider,
  Collapse,
  ListSubheader,
} from '@material-ui/core';
import {
  Skeleton
} from '@material-ui/lab';
import Alert from '@material-ui/lab/Alert';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import EditIcon from '@material-ui/icons/Edit';

import localization from '../utils/localizations';
import {
  IssueReportPanel,
  ThesisDefenseDetail,
  ThesisConsultationHistory,
  StatusTimeline,
  CurrentStatus,
  ThesisDefenseForm,
  ThesisPostDefenseForm,
  DateTimeView,
  FileCommentsHistory,
  AdminNotes,
} from '../views';

import { useStatus } from '../data/Status';
import filterDocumentBy from '../utils/filterDocumentBy';
import filterDocumentsBy from '../utils/filterDocumentsBy';
import grabAllDocuments from '../utils/grabAllDocuments';
import {
  Document
} from '../types';

const appConfig = (window as any).APP_CONFIG;


const useStyles = makeStyles((theme) => ({
  mainBody: {
    padding: 10,
  },
  secondPanel: {
    padding: 10,
    marginTop: 20,
    marginBottom: 20,
  },
  pageTitle: {
    marginTop: 10,
    marginBottom: 10,
  },
  breadcrumbs: {
    margin: 10,
  },
  mainGrid: {
    marginTop: 20,
  },
  gridLabel: {
    textAlign: 'right',
    color: '#555555',
  },
  gridLabelStandard: {
    textAlign: 'right',
  },
  gridLabelBolder: {
    textAlign: 'right',
    fontWeight: 500,
  },
  alignRight: {
    textAlign: 'right',
  },
  ugbOpenMessage: {
    marginTop: 20,
    marginBottom: 20,
  },
  marginRight: {
    marginRight: 10
  }
}));


function SecondSeminarPage() {
  const classes = useStyles();

  const [status, statusLoading, loadStatus] = useStatus();
  const [showForm, setShowForm] = useState(false);

  const showPostDefenseForm = status?.thesis_defense?.status == 'completed';
  let allDocuments: Document[] = [];

  let currentData = undefined;
  let postDefenseData = undefined;
  let editButtonLabel = `${localization.submit} ${localization.thesisDefense}`;
  if (status?.thesis_defense?.id) {
    currentData = {
      ksm: filterDocumentBy(status.thesis_defense.documents, {kind: 'KSM'}),
      thesis_docx: filterDocumentBy(status.thesis_defense.documents, {kind: 'Thesis', content_type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"}),
      thesis_pdf: filterDocumentBy(status.thesis_defense.documents, {kind: 'Thesis', content_type: "application/pdf"}),
      paper_id_pdf: filterDocumentBy(status.thesis_defense.documents, {kind: 'Makalah - Bahasa Indonesia', content_type: "application/pdf"}),
      paper_en_pdf: filterDocumentBy(status.thesis_defense.documents, {kind: 'Makalah - English', content_type: "application/pdf"}),
    }

    postDefenseData = {
      validation_forms: filterDocumentsBy(status.thesis_defense.documents, {kind: 'Halaman Pengesahan Dosen Pembimbing'}),
      thesis_docx: filterDocumentBy(status.thesis_defense.documents, {kind: 'Thesis', content_type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"}),
      thesis_pdf: filterDocumentBy(status.thesis_defense.documents, {kind: 'Thesis', content_type: "application/pdf"}),
      paper_id_pdf: filterDocumentBy(status.thesis_defense.documents, {kind: 'Makalah - Bahasa Indonesia', content_type: "application/pdf"}),
      paper_en_pdf: filterDocumentBy(status.thesis_defense.documents, {kind: 'Makalah - English', content_type: "application/pdf"}),
      resume_id_pdf: filterDocumentBy(status.thesis_defense.documents, {kind: 'Resume - Bahasa Indonesia', content_type: "application/pdf"}),
      resume_en_pdf: filterDocumentBy(status.thesis_defense.documents, {kind: 'Resume - English', content_type: "application/pdf"}),
    }
    allDocuments = grabAllDocuments(status.thesis_defense.documents, status.thesis_defense.status_history);
  }

  if ((status?.thesis_defense?.status == 'submitted') || (status?.thesis_defense?.status == 'scheduled') || (status?.thesis_defense?.status == 'completed') || (status?.thesis_defense?.status == 'canceled')) {
    editButtonLabel = localization.updateData;
  }


  let disableEdit = !(status?.thesis_defense);
  let submissionDeadlineReached = false;
  let deadline = status?.current_ugb_period?.thesis_defense_end;
  if (deadline) {
    if (moment() > moment(deadline)) {
      disableEdit = true;
      submissionDeadlineReached = true;
    }
  }

  if (!deadline) {
    disableEdit = true;
  }

  return (
    <>
    <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs}>
      <Link color="inherit" to={`${appConfig.homepage}`} component={RouterLink}>
        {localization.home}
      </Link>
      <Typography color="textPrimary">{localization.thesisDefense}</Typography>
    </Breadcrumbs>

    <Paper className={classes.mainBody}>
      <ListSubheader style={{paddingLeft: 8}}>{localization.thesisDefensePageTitle}</ListSubheader>

      <Collapse
        in={!showForm}
      >
        {!status.success ? (
          <div style={{position: 'relative', padding: 10}}>
            <Skeleton variant="rect" width={'100%'} height={20} style={{marginBottom: 10}}/>
            <Skeleton variant="rect" width={'100%'} height={20} style={{marginBottom: 40}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
            <Skeleton variant="rect" width={'30%'} height={20} style={{display: 'inline-block', marginBottom: 20, marginRight: 20}}/>
            <Skeleton variant="rect" width={'60%'} height={20} style={{display: 'inline-block', marginBottom: 20}}/>
          </div>
        ) : (
          submissionDeadlineReached ?
          (<Alert
            severity="error"
            className={classes.ugbOpenMessage}
          >
            {localization.submissionHasBeenClosedOn} <DateTimeView value={deadline} format={"Do MMMM YYYY HH:mm:ss"} /> (<DateTimeView value={deadline} fromNow />)
          </Alert>)
          :
          (!!deadline ?<Alert
            severity="info"
            className={classes.ugbOpenMessage}
          >
            {localization.thesisDefenseSubmissionOpenUntil} <DateTimeView value={deadline} format={"Do MMMM YYYY HH:mm:ss"} /> (<DateTimeView value={deadline} fromNow />)
          </Alert>:
          <Alert
            severity="info"
            className={classes.ugbOpenMessage}
          >
            {localization.thesisDefenseSubmissionNotOpen}
          </Alert>)
        )}

        {status && status.thesis_defense && status.thesis_defense.status &&
          <CurrentStatus
            currentStatus={status.thesis_defense.status}
            history={status.thesis_defense.status_history}
            statusMap={[
              {
                status: 'draft',
                label: localization.submission,
                description: localization.awaitingSubmission,
                descriptionCompleted: localization.submissionSent,
              },
              {
                status: 'submitted',
                label: localization.guardianLecturerVerification,
                description: localization.awaitingApproval,
                descriptionCompleted: localization.approved,
              },
              {
                status: 'scheduled',
                label: localization.scheduled,
                description: localization.thesisDefenseScheduled,
              },
              {
                status: 'completed',
                label: localization.completed,
                description: localization.thesisDefenseCompleted,
              },
            ]}
          />
        }

        {status && status.thesis_defense && status.thesis_defense.status &&
          <ThesisDefenseDetail thesisDefense={status.thesis_defense} />
        }
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon/>}
              onClick={(e: any) => {
                setShowForm(true);
              }}
              disabled={disableEdit}
            >{showPostDefenseForm ? localization.postThesisDefense : editButtonLabel}</Button>
          </Grid>
          <Grid item xs={6} className={classes.alignRight}>
            <Button
              variant="contained"
              to={`${appConfig.homepage}`} component={RouterLink}
            >{localization.backToHome}</Button>
          </Grid>
        </Grid>
      </Collapse>

      <Collapse
        in={showForm}
        {...(showForm ? { timeout: 1000 } : {})}
      >
        {!showPostDefenseForm &&
        <ThesisDefenseForm
          onSubmitted={() => {
            setShowForm(false);
            loadStatus();
          }}
          onCancelled={() => {
            setShowForm(false);
          }}
          values={currentData}
        />
        }
        {showPostDefenseForm &&
        <ThesisPostDefenseForm
          onSubmitted={() => {
            setShowForm(false);
            loadStatus();
          }}
          onCancelled={() => {
            setShowForm(false);
          }}
          values={postDefenseData}
        />
        }
      </Collapse>
    </Paper>

    {(status?.thesis_defense?.notes?.length > 0) &&
      <AdminNotes notes={status.thesis_defense.notes} />
    }

    {status && status.thesis_defense && (allDocuments.length > 0) && <FileCommentsHistory documents={allDocuments} /> }
    {status?.thesis_defense && <StatusTimeline history={status.thesis_defense.status_history} /> }

    <IssueReportPanel />
    </>
  );
}

export default SecondSeminarPage;